import { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Loader from './components/Loader';
import Login from './components/Login';
import AdminDrawer from './components/AdminDrawer';
import type { Lang, Translations } from './types';
import { seoData } from './seo';
import { useSEO } from './hooks/useSEO';
import fallbackTranslations from './translations.json';

export default function App() {
  const [lang, setLang] = useState<Lang>('de');
  const [translations, setTranslations] = useState<Translations>(fallbackTranslations.de);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [isAnimationDone, setIsAnimationDone] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  
  const [isAdminRoute, setIsAdminRoute] = useState(window.location.pathname === '/admin');
  const [jwtToken, setJwtToken] = useState<string | null>(localStorage.getItem('jwt_token'));

  const isEditMode = isAdminRoute && !!jwtToken;

  useSEO(translations, lang);

  const fetchTranslations = async (targetLang: Lang) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/translations?lang=${targetLang}`);
      if (response.ok) {
        const data = await response.json();
        setTranslations(data);
      } else {
        setTranslations(fallbackTranslations[targetLang]);
      }
    } catch (error) {
      setTranslations(fallbackTranslations[targetLang]);
    } finally {
      setIsDataLoaded(true);
    }
  };

  useEffect(() => {
    fetchTranslations(lang);
    const handlePopState = () => setIsAdminRoute(window.location.pathname === '/admin');
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleLangChange = (newLang: Lang) => {
    if (newLang === lang) return;
    setShowLoader(true);
    setIsAnimationDone(false);
    setIsDataLoaded(false);
    setTimeout(() => {
      setLang(newLang);
      fetchTranslations(newLang);
    }, 400); 
  };

  const handleAdminUpdate = (key: string, value: string) => {
    setTranslations(prev => ({ ...prev, [key]: value }));
  };

  const saveToDatabase = async () => {
    if (!jwtToken) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/translations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({ lang, data: translations })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        console.error('Szczegóły błędu serwera:', errData);
        throw new Error('Wystąpił błąd przy zapisie na serwerze');
      }
      
      alert("Zapisano pomyślnie!");
    } catch (error) {
      console.error(error);
      alert("Błąd zapisu. Jeśli to błąd, odśwież stronę i spróbuj ponownie.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('jwt_token');
    setJwtToken(null);
    window.history.pushState({}, '', '/');
    setIsAdminRoute(false);
  };

  useEffect(() => {
    if (!showLoader || (isAdminRoute && !jwtToken)) return;
    const fallbackTimer = setTimeout(() => setIsAnimationDone(true), 2500);
    return () => clearTimeout(fallbackTimer);
  }, [showLoader, isAdminRoute, jwtToken]);

  useEffect(() => {
    if (isDataLoaded && isAnimationDone && showLoader) {
      const timer = setTimeout(() => setShowLoader(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isDataLoaded, isAnimationDone, showLoader]);

  useEffect(() => {
    if (showLoader || (isAdminRoute && !jwtToken)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.15 });

    document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [showLoader, isAdminRoute, jwtToken]);

  if (isAdminRoute && !jwtToken) {
    return (
      <Login onLoginSuccess={(token) => {
        localStorage.setItem('jwt_token', token);
        setJwtToken(token);
      }} />
    );
  }

  return (
    <>
      <div className={`fixed inset-0 z-[100] transition-opacity duration-700 ease-in-out ${showLoader ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        {showLoader && <Loader text={translations?.loader_text || 'Prestige Care'} onComplete={() => setIsAnimationDone(true)} />}
      </div>
      
      <div className={`transition-opacity duration-1000 ${showLoader ? 'opacity-0 h-screen overflow-hidden' : 'opacity-100'}`}>
        <Navbar lang={lang} onLangChange={handleLangChange} t={translations} seo={seoData[lang]} isEditable={isEditMode} onUpdate={handleAdminUpdate} />
        <main>
          <Hero t={translations} seo={seoData[lang]} isEditable={isEditMode} onUpdate={handleAdminUpdate} />
          <About t={translations} seo={seoData[lang]} isEditable={isEditMode} onUpdate={handleAdminUpdate} />
          <Services t={translations} seo={seoData[lang]} isEditable={isEditMode} onUpdate={handleAdminUpdate} />
          <Contact t={translations} seo={seoData[lang]} isEditable={isEditMode} onUpdate={handleAdminUpdate} />
        </main>
        <Footer t={translations} seo={seoData[lang]} isEditable={isEditMode} onUpdate={handleAdminUpdate} />

        {isEditMode && (
          <>
            <AdminDrawer t={translations} onUpdate={handleAdminUpdate} onSave={saveToDatabase} onLogout={handleLogout} />
            <button 
              onClick={saveToDatabase}
              className="fixed bottom-6 right-6 z-[100] px-8 py-4 bg-[#c69c6d] text-white font-bold rounded-2xl shadow-2xl hover:scale-105 hover:bg-[#b58b5c] transition-all flex items-center gap-3 cursor-pointer border border-white/20"
            >
              <i className="ri-save-line text-2xl"></i> Zapisz zmiany
            </button>
          </>
        )}
      </div>
    </>
  );
}